import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  FormHelperText,
  Autocomplete
} from '@mui/material';
import { ArrowLeft, Plus, Trash2, Save, ArrowRight, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { computeCodiceFiscale } from '../utils/codiceFiscale';
import { validatePIVA, validateCompanyFiscalCode, normalizePIVA } from '../utils/partitaIva';

const steps = ['Dati Anagrafici', 'Co-intestatari', 'Posizioni Debitorie', 'Riepilogo e Preventivo', 'Piano Pagamenti'];

const PARTY_ROLE_OPTIONS = [
  { value: 'RESPONSABILE_LEGALE', label: 'Responsabile legale' },
  { value: 'COINTESTATARIO', label: 'Co-intestatario' },
  { value: 'SOCIO', label: 'Socio' },
  { value: 'FIDEIUSSORE', label: 'Fideiussore' },
  { value: 'ALTRO', label: 'Altro' }
];

export default function QuoteWizard() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [contactId, setContactId] = useState(null);

  // Step 1: Anagrafica State
  const [contactForm, setContactForm] = useState({
    type: 'individual',
    status: 'lead',
    first_name: '',
    last_name: '',
    company_name: '',
    email: '',
    phone: '',
    address: '',
    city_id: '',
    city: null,
    province: '',
    zip_code: '',
    fiscal_code: '',
    vat_number: '',
    sdi_code: '',
    pec: '',
    birth_date: '',
    birth_place: '',
    birth_place_city: null,
    gender: '',
    notes: ''
  });
  
  // Locations search state
  const [cities, setCities] = useState([]);
  const [citySearch, setCitySearch] = useState('');
  const [cityOpen, setCityOpen] = useState(false);
  const [birthCityOpen, setBirthCityOpen] = useState(false);
  const [loadingCity, setLoadingCity] = useState(false);

  // Step 2: Posizioni State
  const [positions, setPositions] = useState([]);
  const [positionForm, setPositionForm] = useState({
    controparte_id: '',
    tipo_rapporto_id: '',
    contract_number: '',
    original_debt_amount: '',
    notes: ''
  });
  const [controparti, setControparti] = useState([]);
  const [tipiRapporto, setTipiRapporto] = useState([]);

  // Step 3: Quote State
  const [quotePreview, setQuotePreview] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  // Step 4: Payment Plan State
  const [paymentMode, setPaymentMode] = useState('Bonifico Bancario');
  const [installments, setInstallments] = useState([]);
  const [newInstallment, setNewInstallment] = useState({
    amount: '',
    due_date: '',
    payment_method: '',
    note: ''
  });

  // Auto Plan State
  const [autoPlan, setAutoPlan] = useState({
    numInstallments: 2,
    startDate: new Date().toISOString().split('T')[0],
    frequency: 'monthly'
  });

  const [coParties, setCoParties] = useState([]);
  const [newCoParty, setNewCoParty] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    fiscal_code: '',
    role: '',
    gender: '',
    birth_date: '',
    birth_place: '',
    birth_place_city: null
  });
  const [vatValid, setVatValid] = useState(true);
  const [companyFiscalValid, setCompanyFiscalValid] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);

  useEffect(() => {
    fetchOptions();
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      // Fetch all companies. Using limit=1000 to get a good list for autocomplete
      const response = await api.get('/companies?limit=1000');
      console.log('API Companies Response:', response.data);
      if (response.data && response.data.companies) { // Company.findAll returns { companies: [], total: N }
        setCompanies(response.data.companies);
      } else if (response.data && response.data.data) {
        setCompanies(response.data.data);
      } else if (Array.isArray(response.data)) {
        setCompanies(response.data);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  useEffect(() => {
    const autoCalc = async () => {
      if (contactForm.type !== 'individual') return;
      if (!contactForm.first_name || !contactForm.last_name || !contactForm.birth_date || !contactForm.gender || !contactForm.birth_place_city) return;
      const cf = await computeCodiceFiscale({
        first_name: contactForm.first_name,
        last_name: contactForm.last_name,
        birth_date: contactForm.birth_date,
        gender: contactForm.gender,
        birth_place: contactForm.birth_place_city
      });
      if (cf) {
        setContactForm(prev => ({ ...prev, fiscal_code: cf }));
      }
    };
    autoCalc();
  }, [contactForm.first_name, contactForm.last_name, contactForm.birth_date, contactForm.gender, contactForm.birth_place_city, contactForm.type]);

  // Lookup existing contact by Fiscal Code or VAT
  useEffect(() => {
    const lookupContact = async () => {
      // Prevent lookup if we already have an ID (assuming it's the correct one)
      // or if we are loading
      if (contactId) return; 

      let searchParams = {};
      
      if (contactForm.type === 'individual' && contactForm.fiscal_code && contactForm.fiscal_code.length === 16) {
        searchParams.fiscal_code = contactForm.fiscal_code;
      } else if (contactForm.type === 'company') {
        if (contactForm.vat_number && contactForm.vat_number.length === 11) {
          searchParams.vat_number = contactForm.vat_number;
        } else if (contactForm.fiscal_code && contactForm.fiscal_code.length === 11) {
          searchParams.fiscal_code = contactForm.fiscal_code;
        }
      }

      if (Object.keys(searchParams).length === 0) return;

      try {
        const response = await api.get('/contacts/lookup', { params: searchParams });
        const existing = response.data;
        
        if (existing) {
          toast.info('Contatto trovato! I dati sono stati caricati.');
          setContactId(existing.id);
          
          setContactForm(prev => ({
            ...prev,
            status: existing.status || 'lead',
            first_name: existing.first_name || '',
            last_name: existing.last_name || '',
            company_name: existing.company_name || '',
            email: existing.email || '',
            phone: existing.phone || '',
            address: existing.address || '',
            city_id: existing.city_id || '',
            city: existing.city_id ? {
              id: existing.city_id,
              name: existing.city_name,
              province: existing.province,
              postal_code: existing.postal_code || existing.zip_code // handle both naming conventions if any
            } : null,
            province: existing.province || '',
            zip_code: existing.postal_code || '',
            fiscal_code: existing.individual_fiscal_code || existing.company_fiscal_code || prev.fiscal_code,
            vat_number: existing.vat_number || '',
            sdi_code: existing.sdi_code || '',
            pec: existing.pec || '',
            birth_date: existing.birth_date ? existing.birth_date.split('T')[0] : '',
            birth_place: existing.birth_place || '',
            // We might need to fetch the birth city object properly if we want to set birth_place_city
            // But usually birth_place string is what's stored in individuals table if it was simple text, 
            // or we might need to look it up if we store city_id for birth place.
            // Based on Contact model, we have birth_place column in individuals.
            // The frontend uses birth_place_city object for calculation.
            // If existing.birth_place matches a city, we might want to restore it, but it's tricky without ID.
            // For now, keep what we have if it matches, or leave as is.
            gender: existing.gender || '',
            notes: existing.notes || ''
          }));
        }
      } catch (error) {
        // 404 is expected if not found, ignore it
        if (error.response && error.response.status !== 404) {
          console.error('Error looking up contact:', error);
        }
      }
    };

    const timer = setTimeout(lookupContact, 500); // Debounce
    return () => clearTimeout(timer);
  }, [contactForm.fiscal_code, contactForm.vat_number, contactForm.type, contactId]);


  useEffect(() => {
    const autoCalcPartyCF = async () => {
      if (!newCoParty.first_name || !newCoParty.last_name || !newCoParty.birth_date || !newCoParty.gender || !newCoParty.birth_place_city) return;
      const cf = await computeCodiceFiscale({
        first_name: newCoParty.first_name,
        last_name: newCoParty.last_name,
        birth_date: newCoParty.birth_date,
        gender: newCoParty.gender,
        birth_place: newCoParty.birth_place_city
      });
      if (cf) {
        setNewCoParty(prev => ({ ...prev, fiscal_code: cf }));
      }
    };
    autoCalcPartyCF();
  }, [newCoParty.first_name, newCoParty.last_name, newCoParty.birth_date, newCoParty.gender, newCoParty.birth_place_city]);

  // Lookup Co-Party by Fiscal Code
  useEffect(() => {
    const lookupCoParty = async () => {
      if (!newCoParty.fiscal_code || newCoParty.fiscal_code.length !== 16) return;

      try {
        const response = await api.get('/contacts/lookup', { 
          params: { fiscal_code: newCoParty.fiscal_code } 
        });
        const existing = response.data;
        
        if (existing) {
          toast.info('Soggetto trovato! I dati sono stati caricati.');
          
          setNewCoParty(prev => ({
            ...prev,
            first_name: existing.first_name || prev.first_name,
            last_name: existing.last_name || prev.last_name,
            email: existing.email || prev.email,
            phone: existing.phone || prev.phone,
            gender: existing.gender || prev.gender,
            birth_date: existing.birth_date ? existing.birth_date.split('T')[0] : prev.birth_date,
            birth_place_city: existing.city_id ? {
                id: existing.city_id,
                name: existing.city_name,
                province: existing.province
            } : prev.birth_place_city
          }));
        }
      } catch (error) {
        // Ignore 404
      }
    };

    const timer = setTimeout(lookupCoParty, 500);
    return () => clearTimeout(timer);
  }, [newCoParty.fiscal_code]);

  useEffect(() => {
    if (contactForm.type !== 'company') return;
    const vat = normalizePIVA(contactForm.vat_number);
    const cf = normalizePIVA(contactForm.fiscal_code);
    setVatValid(vat.length === 0 || validatePIVA(vat));
    setCompanyFiscalValid(cf.length === 0 || validateCompanyFiscalCode(cf));
    if (!contactForm.fiscal_code && vat.length === 11 && validatePIVA(vat)) {
      setContactForm(prev => ({ ...prev, fiscal_code: vat }));
    }
    if (!contactForm.vat_number && cf.length === 11 && validateCompanyFiscalCode(cf)) {
      setContactForm(prev => ({ ...prev, vat_number: cf }));
    }
  }, [contactForm.type, contactForm.vat_number, contactForm.fiscal_code]);

  const fetchOptions = async () => {
    try {
      const [contropartiRes, tipiRes] = await Promise.all([
        api.get('/controparti?limit=1000'),
        api.get('/tipi-rapporto')
      ]);
      setControparti(contropartiRes.data.controparti || contropartiRes.data);
      setTipiRapporto(tipiRes.data);
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  // --- Step 1 Handlers ---

  const handleContactChange = (field, value) => {
    setContactForm(prev => ({ ...prev, [field]: value }));
  };

  const handleCitySearch = async (event, value) => {
    const query = value || '';
    setCitySearch(query);
    setLoadingCity(true);

    try {
      const response = await api.get('/cities/search', { params: { q: query } });
      setCities(response.data);
    } catch (error) {
      console.error('Error searching cities:', error);
    } finally {
      setLoadingCity(false);
    }
  };

  const handleSubmitContact = async () => {
    setLoading(true);
    try {
      // Basic validation
      if (contactForm.type === 'individual') {
        if (!contactForm.first_name || !contactForm.last_name) {
          toast.error('Nome e Cognome sono obbligatori');
          return;
        }
        if (!contactForm.gender) {
          toast.error('Il sesso è obbligatorio');
          return;
        }
        if (!contactForm.birth_date) {
          toast.error('La data di nascita è obbligatoria');
          return;
        }
        const birth = new Date(contactForm.birth_date);
        const today = new Date();
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
          age--;
        }
        if (age < 18) {
          toast.error('Il contatto deve avere almeno 18 anni');
          return;
        }
        if (!contactForm.fiscal_code) {
          toast.error('Il codice fiscale è obbligatorio');
          return;
        }
        const cf = contactForm.fiscal_code.trim().toUpperCase();
        const cfRegex = /^[A-Z0-9]{16}$/;
        if (!cfRegex.test(cf)) {
          toast.error('Codice fiscale non valido (deve essere di 16 caratteri)');
          return;
        }
        contactForm.fiscal_code = cf;
      }
      if (contactForm.type === 'company' && !contactForm.company_name) {
        toast.error('Ragione Sociale è obbligatoria');
        return;
      }
      if (contactForm.type === 'company') {
        const vat = normalizePIVA(contactForm.vat_number);
        const cfAzienda = normalizePIVA(contactForm.fiscal_code);
        if (contactForm.vat_number && !validatePIVA(vat)) {
          toast.error('Partita IVA non valida');
          return;
        }
        if (contactForm.fiscal_code && !validateCompanyFiscalCode(cfAzienda)) {
          toast.error('Codice Fiscale azienda non valido');
          return;
        }
      }

      if (!contactForm.email) {
        toast.error('L\'email è obbligatoria');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contactForm.email.trim())) {
        toast.error('Indirizzo email non valido');
        return;
      }

      if (!contactForm.phone) {
        toast.error('Il telefono è obbligatorio');
        return;
      }
      const digitsOnly = contactForm.phone.replace(/\D/g, '');
      if (digitsOnly.length < 7) {
        toast.error('Numero di telefono non valido');
        return;
      }

      // Prepare payload with null for empty city_id
      const payload = { ...contactForm };
      if (payload.city_id === '') payload.city_id = null;

      let response;
      if (contactId) {
        response = await api.put(`/contacts/${contactId}`, payload);
        toast.success('Contatto aggiornato');
      } else {
        response = await api.post('/contacts', { ...payload, status: 'lead' });
        setContactId(response.data.id);
        toast.success('Contatto creato');
      }
      
      handleNext();
    } catch (error) {
      console.error('Error saving contact:', error);
      toast.error(error.response?.data?.message || 'Errore nel salvataggio del contatto');
    } finally {
      setLoading(false);
    }
  };

  // --- Step 2 Handlers ---

  const handleAddPosition = async () => {
    if (!positionForm.controparte_id || !positionForm.tipo_rapporto_id || !positionForm.original_debt_amount) {
      toast.error('Compila i campi obbligatori');
      return;
    }

    try {
      const payload = { ...positionForm, contact_id: contactId };
      const response = await api.post(`/contacts/${contactId}/debt-positions`, payload);
      setPositions(prev => [...prev, response.data]);
      setPositionForm({
        controparte_id: '',
        tipo_rapporto_id: '',
        contract_number: '',
        original_debt_amount: '',
        notes: ''
      });
      toast.success('Posizione aggiunta');
    } catch (error) {
      console.error('Error adding position:', error);
      toast.error('Errore nell\'aggiunta della posizione');
    }
  };

  const handleDeletePosition = async (id) => {
    try {
      await api.delete(`/debt-positions/${id}`);
      setPositions(prev => prev.filter(p => p.id !== id));
      toast.success('Posizione rimossa');
    } catch (error) {
      console.error('Error deleting position:', error);
      toast.error('Errore nella rimozione');
    }
  };

  // --- Step 3 Handlers ---

  const loadQuotePreview = async () => {
    setQuoteLoading(true);
    try {
      const response = await api.get(`/contacts/${contactId}/quotes/preview`);
      setQuotePreview(response.data);
    } catch (error) {
      console.error('Error loading preview:', error);
      toast.error('Errore nel calcolo del preventivo');
    } finally {
      setQuoteLoading(false);
    }
  };

  const handleAddCoParty = () => {
    if (!newCoParty.first_name || !newCoParty.last_name) {
      toast.error('Nome e Cognome sono obbligatori');
      return;
    }
    if (!newCoParty.role) {
      toast.error('Seleziona un ruolo');
      return;
    }
    if (!newCoParty.gender) {
      toast.error('Il sesso è obbligatorio');
      return;
    }
    if (!newCoParty.birth_date) {
      toast.error('La data di nascita è obbligatoria');
      return;
    }
    const birth = new Date(newCoParty.birth_date);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    if (age < 18) {
      toast.error('Il soggetto deve avere almeno 18 anni');
      return;
    }
    if (!newCoParty.birth_place_city) {
      toast.error('Il luogo di nascita è obbligatorio');
      return;
    }
    if (!newCoParty.email) {
      toast.error('L\'email è obbligatoria');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newCoParty.email.trim())) {
      toast.error('Indirizzo email non valido');
      return;
    }
    if (!newCoParty.phone) {
      toast.error('Il telefono è obbligatorio');
      return;
    }
    if (!newCoParty.fiscal_code) {
      toast.error('Il codice fiscale è obbligatorio');
      return;
    }
    const cf = newCoParty.fiscal_code.trim().toUpperCase();
    const cfRegex = /^[A-Z0-9]{16}$/;
    if (!cfRegex.test(cf)) {
      toast.error('Codice fiscale non valido (16 caratteri)');
      return;
    }
    setCoParties(prev => [
      ...prev,
      {
        id: Date.now(),
        first_name: newCoParty.first_name,
        last_name: newCoParty.last_name,
        email: newCoParty.email,
        phone: newCoParty.phone,
        fiscal_code: cf,
        role: newCoParty.role,
        gender: newCoParty.gender,
        birth_date: newCoParty.birth_date,
        birth_place: newCoParty.birth_place_city?.name || '',
        birth_place_city: newCoParty.birth_place_city
      }
    ]);
    setNewCoParty({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      fiscal_code: '',
      role: '',
      gender: '',
      birth_date: '',
      birth_place: '',
      birth_place_city: null
    });
  };

  const handleRemoveCoParty = (idToRemove) => {
    setCoParties(prev =>
      prev.filter(p => p.id !== idToRemove)
    );
  };

  const handleFeeChange = (index, newValue) => {
    if (!quotePreview || !quotePreview.items) return;
    
    const updatedItems = [...quotePreview.items];
    updatedItems[index].fee_amount = newValue;
    
    // Recalculate total
    const newTotalFee = updatedItems.reduce((sum, item) => sum + parseFloat(item.fee_amount || 0), 0);
    
    setQuotePreview({
      ...quotePreview,
      items: updatedItems,
      total_fee: newTotalFee
    });
  };

  // --- Step 4 Handlers ---

  const handleAddInstallment = () => {
    if (!newInstallment.amount) {
      toast.error('L\'importo della rata è obbligatorio');
      return;
    }
    if (!newInstallment.due_date) {
      toast.error('La data di scadenza è obbligatoria');
      return;
    }
    
    const amountVal = parseFloat(newInstallment.amount);
    if (isNaN(amountVal) || amountVal <= 0) {
      toast.error('Inserisci un importo valido (maggiore di 0)');
      return;
    }
    
    setInstallments(prev => [
      ...prev,
      {
        id: Date.now(),
        amount: amountVal,
        due_date: newInstallment.due_date,
        payment_method: newInstallment.payment_method || paymentMode,
        note: newInstallment.note
      }
    ]);
    
    setNewInstallment({
      amount: '',
      due_date: '',
      payment_method: '',
      note: ''
    });
    toast.success('Rata aggiunta');
  };

  const handleRemoveInstallment = (id) => {
    setInstallments(prev => prev.filter(i => i.id !== id));
  };

  const handleGeneratePlan = () => {
    if (!quotePreview || !quotePreview.total_fee) return;
    
    const total = quotePreview.total_fee;
    const num = parseInt(autoPlan.numInstallments);
    if (isNaN(num) || num <= 0) {
      toast.error('Inserisci un numero di rate valido');
      return;
    }
    
    if (!autoPlan.startDate) {
      toast.error('Inserisci una data di inizio');
      return;
    }

    const amountPerInstallment = Math.floor((total / num) * 100) / 100;
    const remainder = Math.round((total - (amountPerInstallment * num)) * 100) / 100;
    
    const newInstallments = [];
    let currentDate = new Date(autoPlan.startDate);

    for (let i = 0; i < num; i++) {
      let amount = amountPerInstallment;
      if (i === num - 1) {
        amount += remainder; // Add remainder to last installment
      }

      // Calculate date
      const dateStr = currentDate.toISOString().split('T')[0];
      
      newInstallments.push({
        id: Date.now() + i,
        amount: parseFloat(amount.toFixed(2)),
        due_date: dateStr,
        payment_method: paymentMode,
        note: `Rata ${i + 1}/${num}`
      });

      // Advance date
      if (autoPlan.frequency === 'monthly') {
        currentDate.setMonth(currentDate.getMonth() + 1);
      } else if (autoPlan.frequency === 'weekly') {
        currentDate.setDate(currentDate.getDate() + 7);
      } else if (autoPlan.frequency === 'biweekly') {
        currentDate.setDate(currentDate.getDate() + 14);
      }
    }
    
    setInstallments(newInstallments);
    toast.success('Piano rateale generato');
  };

  const handleUpdateInstallment = (id, field, value) => {
    setInstallments(prev => prev.map(inst => {
      if (inst.id === id) {
        return { ...inst, [field]: value };
      }
      return inst;
    }));
  };

  const handleGenerateQuote = async () => {
    try {
      // Validate installments if any
      const totalInstallments = installments.reduce((sum, i) => sum + parseFloat(i.amount || 0), 0);
      const totalFee = quotePreview.total_fee || 0;
      
      // Allow small float difference
      if (installments.length > 0 && Math.abs(totalInstallments - totalFee) > 0.01) {
        if (!window.confirm(`Attenzione: La somma delle rate (€ ${totalInstallments.toFixed(2)}) non corrisponde al totale del preventivo (€ ${totalFee.toFixed(2)}). Vuoi procedere comunque?`)) {
          return;
        }
      }

      const payload = {
        payment_mode: paymentMode,
        company_id: selectedCompany ? selectedCompany.id : null,
        installments: installments.map(i => ({
          amount: parseFloat(i.amount),
          due_date: i.due_date,
          payment_method: i.payment_method,
          note: i.note
        })),
        items: quotePreview.items.map(item => ({
          debt_position_id: item.debt_position_id,
          fee_amount: item.fee_amount,
          original_fee_amount: item.original_fee_amount,
          fee_rule_id: item.fee_rule_used?.id
        })),
        parties: [
          {
            contact_id: contactId,
            role: 'INTESTATARIO',
            is_primary: true
          },
          ...coParties.map(p => ({
            role: p.role,
            is_primary: false,
            new_contact: {
              type: 'individual',
              status: 'lead',
              first_name: p.first_name || null,
              last_name: p.last_name || null,
              email: p.email || null,
              phone: p.phone || null,
              fiscal_code: p.fiscal_code || null,
              gender: p.gender || null,
              birth_date: p.birth_date || null,
              birth_place: p.birth_place || null
            }
          }))
        ]
      };

      const response = await api.post(`/contacts/${contactId}/quotes`, payload);
      toast.success('Preventivo generato con successo!');
      navigate(`/contacts/${contactId}`); // Go to details page
    } catch (error) {
      console.error('Error generating quote:', error);
      toast.error('Errore nella generazione del preventivo');
    }
  };

  const handleCancel = async () => {
    if (window.confirm('Sei sicuro di voler annullare il preventivo? Tutti i dati inseriti verranno persi.')) {
      try {
        // Delete positions created/added in this session
        if (positions.length > 0) {
          await Promise.all(positions.map(p => api.delete(`/debt-positions/${p.id}`).catch(e => console.warn('Failed to delete position', p.id))));
        }
      } catch (error) {
        console.error('Error cleaning up positions:', error);
      }

      setContactForm({
        type: 'individual',
        status: 'lead',
        first_name: '',
        last_name: '',
        company_name: '',
        email: '',
        phone: '',
        address: '',
        city_id: '',
        city: null,
        province: '',
        zip_code: '',
        fiscal_code: '',
        vat_number: '',
        sdi_code: '',
        pec: '',
        birth_date: '',
        birth_place: '',
        birth_place_city: null,
        gender: '',
        notes: ''
      });
      setContactId(null);
      setPositions([]);
      setPositionForm({
        controparte_id: '',
        tipo_rapporto_id: '',
        contract_number: '',
        original_debt_amount: '',
        notes: ''
      });
      setCoParties([]);
      setNewCoParty({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        fiscal_code: '',
        role: '',
        gender: '',
        birth_date: '',
        birth_place: '',
        birth_place_city: null
      });
      setQuotePreview(null);
      setInstallments([]);
      setPaymentMode('Bonifico Bancario');
      setNewInstallment({
        amount: '',
        due_date: '',
        payment_method: '',
        note: ''
      });
      setActiveStep(0);
      setCitySearch('');
      toast.info('Preventivo annullato e dati resettati');
    }
  };

  // --- Navigation Handlers ---

  const handleNext = () => {
    if (activeStep === 2) {
      if (positions.length === 0) {
        toast.error('Inserisci almeno una posizione debitoria');
        return;
      }
      loadQuotePreview();
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // --- Render Steps ---

  const renderStep1 = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <InputLabel>Tipo Contatto</InputLabel>
          <Select
            value={contactForm.type}
            label="Tipo Contatto"
            onChange={(e) => handleContactChange('type', e.target.value)}
          >
            <MenuItem value="individual">Persona Fisica</MenuItem>
            <MenuItem value="company">Persona Giuridica</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {contactForm.type === 'individual' && (
        <Grid item xs={12}>
          <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>Dati Personali</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nome"
                  value={contactForm.first_name}
                  onChange={(e) => handleContactChange('first_name', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Cognome"
                  value={contactForm.last_name}
                  onChange={(e) => handleContactChange('last_name', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="gender-label">Sesso</InputLabel>
                  <Select
                    labelId="gender-label"
                    required
                    value={contactForm.gender}
                    label="Sesso"
                    onChange={(e) => handleContactChange('gender', e.target.value)}
                  >
                    <MenuItem value="M">Maschio</MenuItem>
                    <MenuItem value="F">Femmina</MenuItem>
                    <MenuItem value="Other">Altro</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Data di Nascita"
                  type="date"
                  required
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ autoComplete: 'off' }}
                  value={contactForm.birth_date || undefined}
                  onChange={(e) => handleContactChange('birth_date', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  id="birth-place-autocomplete"
                  open={birthCityOpen}
                  onOpen={() => {
                    setBirthCityOpen(true);
                    if (cities.length === 0) {
                      handleCitySearch(null, '');
                    }
                  }}
                  onClose={() => setBirthCityOpen(false)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(option) => {
                    const country = option.country || '';
                    const province = (option.province && option.province !== 'null' && option.province !== 'NULL') ? ` (${option.province})` : '';
                    return option.name ? `${option.name}${province} - ${country}` : '';
                  }}
                  options={cities}
                  loading={loadingCity}
                  onInputChange={(event, newInputValue) => {
                    handleCitySearch(event, newInputValue);
                  }}
                  value={contactForm.birth_place_city}
                  onChange={(event, newValue) => {
                    setContactForm(prev => ({
                      ...prev,
                      birth_place_city: newValue,
                      birth_place: newValue ? newValue.name : ''
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Luogo di Nascita"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {loadingCity ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Codice Fiscale"
                  required
                  value={contactForm.fiscal_code}
                  onChange={(e) => handleContactChange('fiscal_code', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      )}

      {contactForm.type === 'company' && (
        <Grid item xs={12}>
          <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>Dati Aziendali</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Ragione Sociale"
                  value={contactForm.company_name}
                  onChange={(e) => handleContactChange('company_name', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Partita IVA"
                  value={contactForm.vat_number}
                  onChange={(e) => handleContactChange('vat_number', e.target.value)}
                  error={!vatValid && !!contactForm.vat_number}
                  helperText={!vatValid && !!contactForm.vat_number ? 'Partita IVA non valida (11 cifre con controllo)' : ''}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Codice Fiscale"
                  value={contactForm.fiscal_code}
                  onChange={(e) => handleContactChange('fiscal_code', e.target.value)}
                  error={!companyFiscalValid && !!contactForm.fiscal_code}
                  helperText={!companyFiscalValid && !!contactForm.fiscal_code ? 'Codice Fiscale azienda non valido (11 cifre con controllo)' : ''}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Codice SDI"
                  value={contactForm.sdi_code}
                  onChange={(e) => handleContactChange('sdi_code', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="PEC"
                  value={contactForm.pec}
                  onChange={(e) => handleContactChange('pec', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      )}

      <Grid item xs={12}>
        <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>Contatti e Indirizzo</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                required
                value={contactForm.email}
                onChange={(e) => handleContactChange('email', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Telefono"
                required
                value={contactForm.phone}
                onChange={(e) => handleContactChange('phone', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Indirizzo"
                value={contactForm.address}
                onChange={(e) => handleContactChange('address', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                id="city-autocomplete"
                open={cityOpen}
                onOpen={() => {
                  setCityOpen(true);
                  if (cities.length === 0) {
                    handleCitySearch(null, '');
                  }
                }}
                onClose={() => setCityOpen(false)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => {
                  const country = option.country || '';
                  const province = (option.province && option.province !== 'null' && option.province !== 'NULL') ? ` (${option.province})` : '';
                  return option.name ? `${option.name}${province} - ${country}` : '';
                }}
                options={cities}
                loading={loadingCity}
                onInputChange={(event, newInputValue) => {
                  handleCitySearch(event, newInputValue);
                }}
                value={contactForm.city}
                onChange={(event, newValue) => {
                  setContactForm(prev => {
                    if (newValue) {
                      return {
                        ...prev,
                        city: newValue,
                        city_id: newValue.id,
                        zip_code: newValue.postal_code,
                        province: newValue.province
                      };
                    } else {
                      return {
                        ...prev,
                        city: null,
                        city_id: '',
                        zip_code: '',
                        province: ''
                      };
                    }
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Città"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {loadingCity ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="CAP"
                value={contactForm.zip_code}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Provincia"
                value={contactForm.province}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Note"
          value={contactForm.notes}
          onChange={(e) => handleContactChange('notes', e.target.value)}
        />
      </Grid>

      <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button
          color="error"
          onClick={handleCancel}
          disabled={loading}
        >
          Annulla
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmitContact}
          endIcon={<ArrowRight />}
          disabled={loading}
        >
          {contactId ? 'Aggiorna e Prosegui' : 'Salva e Prosegui'}
        </Button>
      </Grid>
    </Grid>
  );

  const renderStepCoParties = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Co-intestatari / Coobbligati</Typography>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>Soggetti del Preventivo</Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Intestatario:{' '}
            {contactForm.type === 'company'
              ? contactForm.company_name || `Contatto #${contactId || ''}`
              : `${contactForm.first_name} ${contactForm.last_name}`.trim() || `Contatto #${contactId || ''}`}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Nome"
            size="small"
            value={newCoParty.first_name}
            onChange={(e) => setNewCoParty(prev => ({ ...prev, first_name: e.target.value }))}
            sx={{ minWidth: 160 }}
          />

          <TextField
            label="Cognome"
            size="small"
            value={newCoParty.last_name}
            onChange={(e) => setNewCoParty(prev => ({ ...prev, last_name: e.target.value }))}
            sx={{ minWidth: 160 }}
          />

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Sesso</InputLabel>
            <Select
              value={newCoParty.gender}
              label="Sesso"
              onChange={(e) => setNewCoParty(prev => ({ ...prev, gender: e.target.value }))}
            >
              <MenuItem value="M">Maschio</MenuItem>
              <MenuItem value="F">Femmina</MenuItem>
              <MenuItem value="Other">Altro</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Data di Nascita"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={newCoParty.birth_date || ''}
            onChange={(e) => setNewCoParty(prev => ({ ...prev, birth_date: e.target.value }))}
            sx={{ minWidth: 180 }}
          />

          <Autocomplete
            sx={{ minWidth: 260 }}
            size="small"
            id="coparty-birthplace"
            open={birthCityOpen}
            onOpen={() => {
              setBirthCityOpen(true);
              if (cities.length === 0) {
                handleCitySearch(null, '');
              }
            }}
            onClose={() => setBirthCityOpen(false)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => {
              const country = option.country || '';
              const province = option.province ? ` (${option.province})` : '';
              return option.name ? `${option.name}${province} - ${country}` : '';
            }}
            options={cities}
            loading={loadingCity}
            onInputChange={(event, newInputValue) => {
              handleCitySearch(event, newInputValue);
            }}
            value={newCoParty.birth_place_city}
            onChange={(event, newValue) => {
              setNewCoParty(prev => ({
                ...prev,
                birth_place_city: newValue,
                birth_place: newValue ? newValue.name : ''
              }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Luogo di Nascita"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loadingCity ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />

          <TextField
            label="Email"
            size="small"
            value={newCoParty.email}
            onChange={(e) => setNewCoParty(prev => ({ ...prev, email: e.target.value }))}
            sx={{ minWidth: 200 }}
          />

          <TextField
            label="Telefono"
            size="small"
            value={newCoParty.phone}
            onChange={(e) => setNewCoParty(prev => ({ ...prev, phone: e.target.value }))}
            sx={{ minWidth: 160 }}
          />

          <TextField
            label="Codice Fiscale"
            size="small"
            value={newCoParty.fiscal_code}
            onChange={(e) => setNewCoParty(prev => ({ ...prev, fiscal_code: e.target.value }))}
            sx={{ minWidth: 200 }}
          />

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Ruolo</InputLabel>
            <Select
              value={newCoParty.role}
              label="Ruolo"
              onChange={(e) => setNewCoParty(prev => ({ ...prev, role: e.target.value }))}
            >
              {PARTY_ROLE_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={<Plus size={16} />}
            onClick={handleAddCoParty}
          >
            Aggiungi
          </Button>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Soggetto</TableCell>
                <TableCell>Ruolo</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coParties.map((p) => {
                const name = `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.email || p.phone || 'Soggetto';
                const roleLabel =
                  PARTY_ROLE_OPTIONS.find(r => r.value === p.role)?.label || p.role;
                return (
                  <TableRow key={p.id}>
                    <TableCell>{name}</TableCell>
                    <TableCell>{roleLabel}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveCoParty(p.id)}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {coParties.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    Nessun co-intestatario aggiunto
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Box>
          <Button color="error" onClick={handleCancel} sx={{ mr: 2 }}>Annulla</Button>
          <Button onClick={handleBack} startIcon={<ArrowLeft />}>Indietro</Button>
        </Box>
        <Button
          variant="contained"
          onClick={handleNext}
          endIcon={<ArrowRight />}
        >
          Prosegui
        </Button>
      </Box>
    </Box>
  );

  const renderStep2 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Aggiungi Posizioni Debitorie</Typography>
      
      {/* Add Form */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <Autocomplete
              fullWidth
              size="small"
              options={controparti}
              getOptionLabel={(option) => option.denominazione || ''}
              value={controparti.find(c => c.id === positionForm.controparte_id) || null}
              onChange={(event, newValue) => {
                setPositionForm({ ...positionForm, controparte_id: newValue ? newValue.id : '' });
              }}
              renderInput={(params) => <TextField {...params} label="Controparte" />}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              fullWidth
              size="small"
              options={tipiRapporto}
              getOptionLabel={(option) => option.label || ''}
              value={tipiRapporto.find(t => t.id === positionForm.tipo_rapporto_id) || null}
              onChange={(event, newValue) => {
                setPositionForm({ ...positionForm, tipo_rapporto_id: newValue ? newValue.id : '' });
              }}
              renderInput={(params) => <TextField {...params} label="Tipo Rapporto" />}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Importo"
              type="number"
              value={positionForm.original_debt_amount}
              onChange={(e) => setPositionForm({ ...positionForm, original_debt_amount: e.target.value })}
            />
          </Grid>
           <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              label="N. Contratto"
              value={positionForm.contract_number}
              onChange={(e) => setPositionForm({ ...positionForm, contract_number: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button 
              variant="contained" 
              fullWidth 
              startIcon={<Plus />}
              onClick={handleAddPosition}
            >
              Aggiungi
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* List */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Controparte</TableCell>
              <TableCell>Tipo Rapporto</TableCell>
              <TableCell>N. Contratto</TableCell>
              <TableCell align="right">Importo</TableCell>
              <TableCell align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {positions.map((pos) => (
              <TableRow key={pos.id}>
                <TableCell>{pos.controparte_name || controparti.find(c => c.id === pos.controparte_id)?.denominazione}</TableCell>
                <TableCell>{pos.tipo_rapporto_name || tipiRapporto.find(t => t.id === pos.tipo_rapporto_id)?.label}</TableCell>
                <TableCell>{pos.contract_number || '-'}</TableCell>
                <TableCell align="right">€ {parseFloat(pos.original_debt_amount).toFixed(2)}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="error" onClick={() => handleDeletePosition(pos.id)}>
                    <Trash2 size={16} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {positions.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">Nessuna posizione inserita</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Box>
          <Button color="error" onClick={handleCancel} sx={{ mr: 2 }}>Annulla</Button>
          <Button onClick={handleBack} startIcon={<ArrowLeft />}>Indietro</Button>
        </Box>
        <Button 
          variant="contained" 
          onClick={handleNext} 
          endIcon={<ArrowRight />}
          disabled={positions.length === 0}
        >
          Calcola Preventivo
        </Button>
      </Box>
    </Box>
  );

  const renderStep3 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Riepilogo e Calcolo</Typography>
      
      {quoteLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : quotePreview ? (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom color="primary">
            Tabellario Applicato: <strong>{quotePreview.fee_config?.name || 'Standard'}</strong>
          </Typography>

          <TableContainer sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Posizione</TableCell>
                  <TableCell align="right">Debito Orig.</TableCell>
                  <TableCell align="right">Compenso</TableCell>
                  <TableCell>Dettaglio Regola</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quotePreview.items.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      {item.debt_position.controparte_name} - {item.debt_position.tipo_rapporto_name}
                    </TableCell>
                    <TableCell align="right">€ {item.debt_amount.toFixed(2)}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '100%' }}>
                        <TextField
                          type="number"
                          size="small"
                          value={item.fee_amount}
                          onChange={(e) => handleFeeChange(idx, e.target.value)}
                          InputProps={{
                            startAdornment: <Box component="span" sx={{ mr: 0.5, color: 'text.secondary' }}>€</Box>,
                          }}
                          sx={{ width: 120 }}
                        />
                        {(item.original_fee_amount !== undefined && item.original_fee_amount !== null) ? (
                          <Typography variant="body2" sx={{ mt: 0.5, display: 'block', width: '100%', textAlign: 'right', fontSize: '0.75rem' }}>
                            {Math.abs(parseFloat(item.fee_amount) - parseFloat(item.original_fee_amount)) > 0.01 ? (
                              <span style={{ color: '#ed6c02', fontWeight: 'bold' }}>
                                Calc: € {parseFloat(item.original_fee_amount).toFixed(2)}
                              </span>
                            ) : (
                              <span style={{ color: '#666' }}>
                                Calc: € {parseFloat(item.original_fee_amount).toFixed(2)}
                              </span>
                            )}
                          </Typography>
                        ) : (
                          <Typography variant="caption" sx={{ color: 'red' }}>Err: No Orig Fee</Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                      {item.fee_rule_used ? (
                        <>
                          {item.fee_rule_used.tipo_rapporto_label || 'Generica'} <br/>
                          (Fisso: €{item.fee_rule_used.fixed_fee} + {item.fee_rule_used.variable_fee_percent}%)
                        </>
                      ) : 'Default'}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'action.hover' : '#f8fafc' }}>
                  <TableCell><strong>TOTALE</strong></TableCell>
                  <TableCell align="right"><strong>€ {quotePreview.total_debt.toFixed(2)}</strong></TableCell>
                  <TableCell align="right"><strong>€ {quotePreview.total_fee.toFixed(2)}</strong></TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <Typography color="error">Errore nel caricamento del preventivo.</Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Box>
          <Button color="error" onClick={handleCancel} sx={{ mr: 2 }}>Annulla</Button>
          <Button onClick={handleBack} startIcon={<ArrowLeft />}>Indietro</Button>
        </Box>
        <Button 
          variant="contained" 
          onClick={handleNext} 
          endIcon={<ArrowRight />}
          size="large"
          disabled={!quotePreview}
        >
          Prosegui al Piano Pagamenti
        </Button>
      </Box>
    </Box>
  );

  const renderStep4 = () => {
    const totalFee = quotePreview?.total_fee || 0;
    const totalInstallments = installments.reduce((sum, i) => sum + parseFloat(i.amount || 0), 0);
    const remaining = totalFee - totalInstallments;
    
    return (
      <Box>
        <Typography variant="h6" gutterBottom>Piano Pagamenti</Typography>
        
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>Riepilogo Costi</Typography>
              <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'action.hover' : '#f8fafc', borderRadius: 1 }}>
                <Typography variant="body1">Totale Preventivo: <strong>€ {totalFee.toFixed(2)}</strong></Typography>
                <Typography variant="body2" color={remaining > 0.01 ? 'warning.main' : remaining < -0.01 ? 'error.main' : 'success.main'}>
                  Da coprire: € {remaining.toFixed(2)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputLabel>Modalità di Pagamento Principale</InputLabel>
                <Select
                  value={paymentMode}
                  label="Modalità di Pagamento Principale"
                  onChange={(e) => setPaymentMode(e.target.value)}
                >
                  <MenuItem value="Bonifico Bancario">Bonifico Bancario</MenuItem>
                  <MenuItem value="RID">RID</MenuItem>
                  <MenuItem value="Assegno">Assegno</MenuItem>
                  <MenuItem value="Contanti">Contanti</MenuItem>
                  <MenuItem value="POS">POS</MenuItem>
                  <MenuItem value="Altro">Altro</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mt: 3 }}>
                <Autocomplete
                  id="company-select"
                  options={companies}
                  getOptionLabel={(option) => option.company_name || ''}
                  value={selectedCompany}
                  onChange={(event, newValue) => {
                    setSelectedCompany(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="Contratto con" 
                      helperText="Seleziona l'azienda con cui viene stipulato il contratto"
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        <Typography variant="subtitle1" gutterBottom>Piano Rateale (Opzionale)</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Aggiungi le scadenze se il pagamento è rateizzato.
        </Typography>

        <Paper sx={{ p: 2, mb: 3, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'action.hover' : '#f0f9ff' }} variant="outlined">
          <Typography variant="subtitle2" gutterBottom color="primary">Generazione Automatica Piano</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Numero Rate"
                type="number"
                value={autoPlan.numInstallments}
                onChange={(e) => setAutoPlan({ ...autoPlan, numInstallments: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Data Prima Rata"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={autoPlan.startDate}
                onChange={(e) => setAutoPlan({ ...autoPlan, startDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Frequenza</InputLabel>
                <Select
                  value={autoPlan.frequency}
                  label="Frequenza"
                  onChange={(e) => setAutoPlan({ ...autoPlan, frequency: e.target.value })}
                >
                  <MenuItem value="monthly">Mensile</MenuItem>
                  <MenuItem value="weekly">Settimanale</MenuItem>
                  <MenuItem value="biweekly">Quindicinale</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button 
                variant="outlined" 
                fullWidth 
                onClick={handleGeneratePlan}
              >
                Genera Piano
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Importo Rata"
                type="number"
                value={newInstallment.amount}
                onChange={(e) => setNewInstallment({ ...newInstallment, amount: e.target.value })}
                InputProps={{
                  startAdornment: <Box component="span" sx={{ mr: 0.5, color: 'text.secondary' }}>€</Box>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Scadenza"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={newInstallment.due_date}
                onChange={(e) => setNewInstallment({ ...newInstallment, due_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Note (es. Acconto)"
                value={newInstallment.note}
                onChange={(e) => setNewInstallment({ ...newInstallment, note: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button 
                variant="contained" 
                fullWidth 
                startIcon={<Plus />}
                onClick={handleAddInstallment}
              >
                Aggiungi Rata
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Scadenza</TableCell>
                <TableCell align="right">Importo</TableCell>
                <TableCell>Metodo</TableCell>
                <TableCell>Note</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {installments.map((inst) => (
                <TableRow key={inst.id}>
                  <TableCell>
                    <TextField
                      type="date"
                      size="small"
                      value={inst.due_date}
                      onChange={(e) => handleUpdateInstallment(inst.id, 'due_date', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 150 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      value={inst.amount}
                      onChange={(e) => handleUpdateInstallment(inst.id, 'amount', e.target.value)}
                      InputProps={{ 
                        startAdornment: <Box component="span" sx={{ mr: 0.5, color: 'text.secondary' }}>€</Box> 
                      }}
                      sx={{ width: 120 }}
                    />
                  </TableCell>
                  <TableCell>{inst.payment_method}</TableCell>
                  <TableCell>{inst.note}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="error" onClick={() => handleRemoveInstallment(inst.id)}>
                      <Trash2 size={16} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {installments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">Nessuna rata inserita (Pagamento unico)</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Box>
            <Button color="error" onClick={handleCancel} sx={{ mr: 2 }}>Annulla</Button>
            <Button onClick={handleBack} startIcon={<ArrowLeft />}>Indietro</Button>
          </Box>
          <Button 
            variant="contained" 
            color="success" 
            onClick={handleGenerateQuote} 
            startIcon={<CheckCircle />}
            size="large"
          >
            Conferma e Genera
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>Nuova Pratica</Typography>
        <Typography variant="body2" color="text.secondary">
          Wizard guidato per la creazione di un nuovo contatto e generazione preventivo
        </Typography>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card>
        <CardContent>
          {activeStep === 0 && renderStep1()}
          {activeStep === 1 && renderStepCoParties()}
          {activeStep === 2 && renderStep2()}
          {activeStep === 3 && renderStep3()}
          {activeStep === 4 && renderStep4()}
        </CardContent>
      </Card>
    </Box>
  );
}
